{
  class GMap2 extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback(){
      // user-controlled vars
      this._clusterer = null // a marker cluster object
      this._apiKey = this.getAttribute('api-key')
      this._mapId = this.getAttribute('map-id')
      this._centerLat = this.hasAttribute('center-lat') ? parseFloat(this.getAttribute('center-lat')) : 42.3314
      this._centerLng = this.hasAttribute('center-lng') ? parseFloat(this.getAttribute('center-lng')) : -83.0458
      this._initialZoom = this.hasAttribute('initial-zoom') ? parseInt(this.getAttribute('initial-zoom')) : 12
      this._minimumZoom = this.hasAttribute('minimum-zoom') ? parseInt(this.getAttribute('minimum-zoom')) : 9999
      this._aspectRatio = this.hasAttribute('aspect-ratio') ? this.getAttribute('aspect-ratio') : "16/9"
      this._zoomToFitMarkers = this.hasAttribute('zoom-to-fit-markers') ? this.getAttribute('zoom-to-fit-markers') == 'true' : true
      this._markerInteractionEvents = this.hasAttribute('marker-interaction-events') ? this.getAttribute('marker-interaction-events') : "click" // or "hover" or "click|hover" or "click" etc
      // these can be CSS variables too!
      this._clusterBackgroundColor = this.hasAttribute('cluster-background-color') ? this.getAttribute('cluster-background-color') : '#000'
      this._clusterTextColor = this.hasAttribute('cluster-text-color') ? this.getAttribute('cluster-text-color') : '#fff'
      
      if(!this._mapId){
        console.error("<g-map-2> must have a `map-id` attribute for markers to work and for styles to be applied via Google console")
        return
      }

      // private vars
      this._map = null // a gmap object
      this._markers = [] // an array of gmap marker objects
      this._openInfoWindow = null // an infowindow object
      this.querySelectorAll('[id^="map-"]').forEach(el => el.remove())
      this._mapEl = document.createElement('div')
      
      // create the map DOM element
      this._mapEl.id = "map-" + Math.random().toString(16).slice(2)
      this._mapEl.style.aspectRatio = this._aspectRatio
      this._mapEl.style.width = "100%"
      this._mapEl.style.backgroundColor = "#ccc"
      this.append(this._mapEl)
      this.style.display = 'flex'
      this.querySelectorAll('g-marker').forEach(gm => gm.style.display = 'none')
      
      // lazy load google script
      if(this.getAttribute('loading') == 'eager'){
        this._loadMap()
      } else {
        const observer = new IntersectionObserver(entries => {
          if(entries[0].isIntersecting && !this._map){
            this._loadMap()
          }
        });
        observer.observe(this);
      }

      // watch for marker changes
      const mutationObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if(this._map){
            this._addMarkers()
          }
        });    
      });    
      mutationObserver.observe(this, { childList: true });

    }
    
    _loadMap(callback = null){
      if(!document.querySelector('script[src*="https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js"]')){
        const clusterScript = document.createElement('script')
        clusterScript.src = "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js"
        clusterScript.onload = () => {
          const gmaps = document.createElement('script')
          gmaps.src = `https://maps.googleapis.com/maps/api/js?callback=_gmapCustomElementLoad&libraries=marker&loading=async&key=${this._apiKey}`
          document.head.appendChild(gmaps)
        }
        document.head.appendChild(clusterScript)

        // const gmaps = document.createElement('script')
        // gmaps.src = `https://maps.googleapis.com/maps/api/js?callback=_gmapCustomElementLoad&libraries=marker&loading=async&key=${this._apiKey}`
        window._gmapCustomElementLoad = () => {
          this._map = new google.maps.Map(this._mapEl, {
            center: new google.maps.LatLng(this._centerLat, this._centerLng),
            zoom: this._initialZoom,
            mapId: this._mapId
          })
          this._addMarkers()
          if(typeof callback == 'function'){
            callback()
          }
          google.maps.event.addListenerOnce(this._map, 'bounds_changed', () => {
            if(this._map.getZoom() > this._minimumZoom){
              this._map.setZoom(this._minimumZoom)
            }
          })
        }
        // const head = document.querySelector('head')
        // head.insertBefore(gmaps, head.lastChild)
      } else {
        if(!this._map){
          if(typeof google === 'undefined'){
            setTimeout(() => this._loadMap(callback), 1000)
            return
          } 
          this._map = new google.maps.Map(this._mapEl, {
            center: new google.maps.LatLng(this._centerLat, this._centerLng),
            zoom: this._initialZoom,
            mapId: this._mapId
          })
        }
        this._addMarkers()
        if(typeof callback == 'function'){
          callback()
        }
        google.maps.event.addListenerOnce(this._map, 'bounds_changed', () => {
          if(this._map.getZoom() > this._minimumZoom){
            this._map.setZoom(this._minimumZoom)
          }
        })        
      }
    }
    
    _addMarkers(){
      this.querySelectorAll('g-marker').forEach(gm => gm.style.display = 'none')
      this._markers.forEach(marker => marker.map = null)
      this._markers = []

      this.querySelectorAll('g-marker').forEach(markerSlot => {
        const lat = markerSlot.getAttribute('lat')
        const lng = markerSlot.getAttribute('lng')

        if(!lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))){
          return
        }

        const iconUrl = markerSlot.getAttribute('icon-url')

        const marker = new google.maps.marker.AdvancedMarkerElement({
          map: this._map,
          position: { lat: parseFloat(lat), lng: parseFloat(lng) },
          ...(iconUrl && {
            content: Object.assign(document.createElement('img'), { src: iconUrl, alt: markerSlot.getAttribute('alt') })
          })
        })
        
        if (markerSlot.innerHTML || markerSlot.hasAttribute('ada-modal-id')) {
          const adaModalId = markerSlot.getAttribute('ada-modal-id')

          if (adaModalId) {
            // ada-modal
            marker.addListener('click', () => {
              const foundModal = document.getElementById(adaModalId)
              if (foundModal && typeof foundModal.openModal === 'function') {
                // close any open InfoWindow first
                if (this._openInfoWindow) {
                  this._openInfoWindow.close()
                  this._openInfoWindow = null
                }
                foundModal.openModal()
              } else {
                console.warn(`<g-map-2>: Modal with id "${adaModalId}" not found or has no openModal() method.`)
              }
            })
          } else {
            // standard info window - if ada-modal attr is not set
            const infoWindow = new google.maps.InfoWindow({ 
              content: markerSlot.innerHTML 
            })

            const closeOpenedInfoWindow = () => {
              if (this._openInfoWindow) {
                this._openInfoWindow.close()
                this._openInfoWindow = null
              }
            }

            const openInfoWindow = () => {
              this._openInfoWindow = infoWindow
              this._openInfoWindow.open({ anchor: marker, map: this._map })
            }

            if(this._markerInteractionEvents.indexOf("hover") != -1){
              marker.content.addEventListener('mouseenter', () => {
                if(this._openInfoWindow != infoWindow){
                  closeOpenedInfoWindow()
                  openInfoWindow()
                }
              })
            }

            if(this._markerInteractionEvents.indexOf("click") != -1){
              marker.addListener('click', () => {
                if(this._openInfoWindow == infoWindow){
                  closeOpenedInfoWindow()
                } else {
                  closeOpenedInfoWindow()
                  openInfoWindow()
                }
              })
            }
            
            if(markerSlot.getAttribute('initially-open') == 'true'){
              openInfoWindow()
            }
          }
        }

        this._markers.push(marker)    
      })
      
      if(!this._clusterer){
        this._clusterer = new markerClusterer.MarkerClusterer({
          map: this._map,
          markers: this._markers,
          renderer: {
            render: ({ count, position }, stats) => {
              const content = document.createElement("div");
              content.style.position = "relative";
              content.innerHTML = `
                <div style="
                  background: ${this._clusterBackgroundColor};
                  color: ${this._clusterTextColor};
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  font-size: 1rem;
                  font-weight: 600;
                  width: 2.5rem;
                  height: 2.5rem;
                  border-radius: 50%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                ">${count}</div>
              `;
              return new google.maps.marker.AdvancedMarkerElement({
                position,
                content
              });
            }
          }
        });
      } else {
        this._clusterer.clearMarkers()
        this._clusterer.addMarkers(this._markers)
      }

      setTimeout(() => {
        if(this._markers.length && this._zoomToFitMarkers){
          const bounds = new google.maps.LatLngBounds()
          this._markers.forEach(marker => bounds.extend(marker.position))
          this._map.fitBounds(bounds)
        }

        setTimeout(() => {
          if(this._map.getZoom() > this._minimumZoom){
            this._map.setZoom(this._minimumZoom)
          }
        }, 1000)
      })
    }
    
    popup(index){
      if(!this._map){
        this._loadMap(() => {
          const marker = this._markers[index]
          new google.maps.event.trigger( marker, 'click')
          this.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
      } else {
        const marker = this._markers[index]
        new google.maps.event.trigger( marker, 'click')
        this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  customElements.define('g-map-2', GMap2)
}